import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, getDocs } from "firebase/firestore";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { auth, db } from "../../firebase/firebase.js";
import EditInstructionDialog from "./EditInstructionDialog.jsx";

const AgentsPage = () => {
  const uid = auth.currentUser?.uid;
  const [coaches, setCoaches] = useState([]);
  const [coreMap, setCoreMap] = useState({});
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null); // coach object
  const theme = useTheme();

  const accentTone = useMemo(
    () => theme.palette.secondary?.main ?? theme.palette.primary.light ?? theme.palette.primary.main,
    [theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary?.main]
  );

  const filteredCoaches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return coaches;
    }
    return coaches.filter((coach) => {
      const name = (coach.name || coach.id || "").toLowerCase();
      const desc = (coach.description || coach.instructions || "").toLowerCase();
      return name.includes(term) || desc.includes(term);
    });
  }, [coaches, search]);

  const pageBackground = useMemo(() => {
    const base = theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.background.default;
    const primaryGlow = alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.12 : 0.35);
    const accentGlow = alpha(accentTone, theme.palette.mode === "light" ? 0.1 : 0.3);
    return `radial-gradient(circle at 0% -20%, ${primaryGlow} 0%, transparent 55%), radial-gradient(circle at 120% -10%, ${accentGlow} 0%, transparent 50%), ${base}`;
  }, [accentTone, theme]);

  const filteredCount = filteredCoaches.length;
  const totalCoaches = coaches.length;
  const hasSearch = Boolean(search.trim());

  useEffect(() => {
    const loadCore = async () => {
      const snap = await getDocs(collection(db, "coreAgents"));
      const map = {};
      snap.docs.forEach((d) => (map[d.id] = d.data()));
      setCoreMap(map);
    };
    loadCore();
  }, []);

  useEffect(() => {
    if (!uid) return;
    // live subscription – updates instantly when any agent doc changes
    const unsub = onSnapshot(collection(db, "users", uid, "agents"), (snap) => {
      setCoaches(
        snap.docs
          .map((d) => ({
            id: d.id,
            ...(coreMap[d.id] || {}),
            ...d.data(),
          }))
          .filter(
            (agent) =>
              agent.id !== "coordinator_router" &&
              agent.id !== "coordinator_profile"
          )
      );
    });
    return unsub;
  }, [uid, coreMap]);

  return (
    <Box
      sx={{
        minHeight: "100%",
        py: { xs: 2.5, md: 4 },
        px: { xs: 2, md: 4 },
        background: pageBackground,
        transition: "background 320ms ease",
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 3,
            px: { xs: 2.75, md: 3.5 },
            py: { xs: 3, md: 3.75 },
            color: (theme) =>
              theme.palette.mode === "light"
                ? theme.palette.text.primary
                : theme.palette.common.white,
            background: (theme) =>
              theme.palette.mode === "light"
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(accentTone, 0.1)} 35%, ${alpha(theme.palette.background.paper, 0.92)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.42)} 0%, ${alpha(accentTone, 0.33)} 35%, ${alpha(theme.palette.background.paper, 0.72)} 100%)`,
            border: (theme) =>
              `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.22 : 0.45)}`,
            boxShadow: (theme) => theme.shadows[5],
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background: (theme) =>
                theme.palette.mode === "light"
                  ? `radial-gradient(circle at 18% 22%, ${alpha(theme.palette.common.white, 0.54)} 0%, transparent 55%)`
                  : `radial-gradient(circle at 18% 22%, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 0.28)} 0%, transparent 60%)`,
            }}
          />
          <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Typography variant="overline" sx={{ letterSpacing: 1.6, opacity: 0.72 }}>
              AI COACH STUDIO
            </Typography>
            <Typography
              variant="h4"
              component="h1"
              data-testid="agents-page-title"
              sx={{ fontWeight: 700 }}
            >
              Review & customise your coaches
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: 720,
                color:
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.text.primary, 0.8)
                    : alpha(theme.palette.common.white, 0.9),
              }}
            >
              Fine tune how each specialist shows up for members. Adjust their tone, area of focus, and
              instructions so your AI coaching team feels personal and aligned with your goals.
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            px: { xs: 2.5, md: 3 },
            py: { xs: 2, md: 2.25 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            gap: { xs: 1.5, md: 2.5 },
            background: (theme) =>
              theme.palette.mode === "light"
                ? alpha(theme.palette.background.paper, 0.92)
                : alpha(theme.palette.background.paper, 0.78),
            border: (theme) => `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "light" ? 0.6 : 0.45)}`,
            boxShadow: (theme) => theme.shadows[3],
            backdropFilter: "blur(18px)",
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            size="medium"
            placeholder="Search coaches by name or speciality"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            data-testid="agents-search-input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.common.white, 0.95)
                    : alpha(theme.palette.background.default, 0.6),
                '& fieldset': {
                  borderColor: alpha(theme.palette.text.primary, 0.12),
                },
                '&:hover fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.45),
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1.4,
                },
              },
            }}
          />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            sx={{ minWidth: { md: 220 } }}
          >
            <Typography variant="body2" sx={{ color: alpha(theme.palette.text.primary, 0.7) }}>
              {hasSearch ? filteredCount : totalCoaches} coach{(hasSearch ? filteredCount : totalCoaches) === 1 ? "" : "es"}
            </Typography>
            {hasSearch && (
              <Typography variant="body2" sx={{ color: alpha(theme.palette.text.primary, 0.5) }}>
                {filteredCount === 0 ? "No match" : `of ${totalCoaches}`}
              </Typography>
            )}
          </Stack>
        </Paper>

        {filteredCount === 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              px: { xs: 2.5, md: 3 },
              py: { xs: 5, md: 6 },
              textAlign: "center",
              background: (theme) =>
                theme.palette.mode === "light"
                  ? alpha(theme.palette.background.paper, 0.9)
                  : alpha(theme.palette.background.paper, 0.7),
              border: (theme) => `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
              boxShadow: (theme) => theme.shadows[2],
            }}
          >
            <Typography
              variant="body1"
              data-testid="agents-no-agents-message"
              sx={{
                maxWidth: 520,
                mx: "auto",
                color:
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.text.primary, 0.75)
                    : alpha(theme.palette.common.white, 0.85),
              }}
            >
              No agents found matching your search criteria. Try a different keyword or clear the search to view
              your full coaching roster.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 2.5, md: 3 }}>
            {filteredCoaches.map((agent, index) => {
              const displayName = agent.name || agent.id;
              const blurb = agent.description || agent.instructions || "No description provided yet.";
              const initials = (displayName || "?").slice(0, 2).toUpperCase();
              return (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={agent.id}
                  data-testid={index === 0 ? "agent-card-0" : undefined}
                >
                  <Card
                    elevation={0}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: 3,
                      border: (theme) =>
                        `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.25 : 0.4)}`,
                      background: (theme) =>
                        theme.palette.mode === "light"
                          ? `linear-gradient(140deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(accentTone, 0.08)} 120%)`
                          : `linear-gradient(140deg, ${alpha(theme.palette.primary.dark, 0.55)} 0%, ${alpha(accentTone, 0.38)} 120%)`,
                      boxShadow: (theme) => theme.shadows[6],
                      backdropFilter: "blur(18px)",
                      transition: "transform 200ms ease, box-shadow 200ms ease",
                      '&:hover': {
                        transform: "translateY(-4px)",
                        boxShadow: (theme) => theme.shadows[10],
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        background: (theme) =>
                          theme.palette.mode === "light"
                            ? `radial-gradient(circle at -10% 20%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 65%)`
                            : `radial-gradient(circle at -10% 20%, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 0.28)} 0%, transparent 65%)`,
                        opacity: 0.9,
                      }}
                    />
                    <CardContent
                      sx={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.75,
                        px: { xs: 2.5, md: 3 },
                        pt: { xs: 2.5, md: 3 },
                        pb: 0,
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          src={agent.photoURL || undefined}
                          alt={displayName}
                          data-testid={agent.photoURL ? "agent-card-photo" : undefined}
                          sx={{
                            width: 56,
                            height: 56,
                            fontSize: 22,
                            fontWeight: 600,
                            color: theme.palette.primary.contrastText,
                            backgroundImage: (theme) =>
                              agent.photoURL
                                ? "none"
                                : theme.palette.mode === "light"
                                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)}, ${alpha(accentTone, 0.85)})`
                                : `linear-gradient(135deg, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 0.9)}, ${alpha(accentTone, 0.8)})`,
                            border: (theme) =>
                              `2px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.35 : 0.5)}`,
                            boxShadow: (theme) => theme.shadows[4],
                          }}
                        >
                          {!agent.photoURL ? initials : null}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            data-testid="agent-card-name"
                            sx={{
                              fontWeight: 700,
                              letterSpacing: 0.2,
                              color:
                                theme.palette.mode === "light"
                                  ? alpha(theme.palette.text.primary, 0.88)
                                  : alpha(theme.palette.common.white, 0.92),
                            }}
                          >
                            {displayName}
                          </Typography>
                          <Typography
                            variant="body2"
                            data-testid="agent-card-description"
                            sx={{
                              mt: 0.5,
                              color:
                                theme.palette.mode === "light"
                                  ? alpha(theme.palette.text.primary, 0.65)
                                  : alpha(theme.palette.common.white, 0.75),
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {blurb}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                    <CardActions
                      sx={{
                        position: "relative",
                        px: { xs: 2.5, md: 3 },
                        py: { xs: 2.2, md: 2.4 },
                        pt: { xs: 1.5, md: 1.75 },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: { xs: 1.25, sm: 1.5 },
                        alignItems: { xs: "stretch", sm: "center" },
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          letterSpacing: 0.35,
                          textTransform: "uppercase",
                          color:
                            theme.palette.mode === "light"
                              ? alpha(theme.palette.text.primary, 0.55)
                              : alpha(theme.palette.common.white, 0.65),
                        }}
                      >
                        refine persona & guidance
                      </Typography>
                      <Button
                        size="medium"
                        variant="contained"
                        data-testid="agent-card-edit-button"
                        onClick={() => setEditTarget(agent)}
                        startIcon={<EditOutlinedIcon fontSize="small" />}
                        sx={{
                          borderRadius: 999,
                          px: { xs: 2.4, md: 2.8 },
                          py: 1,
                          fontWeight: 600,
                          letterSpacing: 0.35,
                          backgroundImage: (theme) =>
                            theme.palette.mode === "light"
                              ? `linear-gradient(120deg, ${theme.palette.primary.main}, ${accentTone})`
                              : `linear-gradient(120deg, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 0.94)}, ${alpha(accentTone, 0.88)})`,
                          boxShadow: (theme) => theme.shadows[6],
                          '&:hover': {
                            backgroundImage: (theme) =>
                              theme.palette.mode === "light"
                                ? `linear-gradient(120deg, ${theme.palette.primary.dark}, ${accentTone})`
                                : `linear-gradient(120deg, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 1)}, ${alpha(accentTone, 0.95)})`,
                            boxShadow: (theme) => theme.shadows[9],
                          },
                        }}
                      >
                        Edit Coach
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {editTarget && (
        <EditInstructionDialog
          open
          coach={editTarget}
          uid={uid}
          onClose={() => setEditTarget(null)}
        />
      )}
    </Box>
  );
};

export default AgentsPage;
